import { createMongoAbility } from "@casl/ability";
import { useAbility } from "@casl/react";
import axios from "axios";

import { AbilityContext } from "../contexts/AbilityContext";

// Singleton ability instance to update dynamically
export const ability = createMongoAbility();

export const updateAbility = (rules: any[]) => {
  ability.update(rules);
};

let fetchCounter = 0;

export const fetchCapabilities = async (projectId?: string) => {
  fetchCounter += 1;
  const currentFetchId = fetchCounter;
  try {
    let effectiveProjectId = projectId;
    if (!effectiveProjectId) {
      const match = window.location.pathname.match(/\/projects\/([^/]+)/);
      if (match && match[1] !== "select_cloud_files") {
        effectiveProjectId = match[1];
      }
    }

    const url = effectiveProjectId ? `/user/capabilities?projectId=${effectiveProjectId}` : "/user/capabilities";
    const response = await axios.get(url);

    if (currentFetchId === fetchCounter) {
      if (response.data && response.data.rules) {
        updateAbility(response.data.rules);
      }
    }
  } catch (error) {
    if (currentFetchId === fetchCounter) {
      console.error("Failed to fetch capabilities", error);
      updateAbility([]);
    }
  }
};

export const usePermissions = () => useAbility(AbilityContext);

/**
 * Canonical derived role flags — import these instead of re-deriving per-component.
 *
 * Derivation rules (CASL-only, no raw role strings):
 *   isAdmin        → ability.can("manage", "all") — only system admin has this
 *   isOrgManager   → can create/delete Organization (granted to org managers + admins)
 *   isProjectManager → can update Project
 *   isMember       → can create Story
 *   isGuest        → no permissions (only when auth state is guest)
 *   isViewOnly     → none of the above (read-only access)
 */
export const useRoleFlags = () => {
  const currentAbility = usePermissions();
  const isAdmin = currentAbility.can("manage", "all");
  const isOrgManager = currentAbility.can("create", "Organization") || currentAbility.can("delete", "Organization");
  const isProjectManager = currentAbility.can("update", "Project");
  const isMember = currentAbility.can("create", "Story");
  const isGuest = !isAdmin && !isOrgManager && !isProjectManager && !isMember && currentAbility.rules.length === 0;
  const isViewOnly = !isAdmin && !isOrgManager && !isProjectManager && !isMember && !isGuest;
  return { ability: currentAbility, isAdmin, isOrgManager, isProjectManager, isMember, isViewOnly, isGuest };
};
